import { useRuntimeVariables } from 'app-kit'
import rawAxios, { AxiosError, AxiosResponse, HttpStatusCode } from 'axios'
import { GeckoAuthContext } from 'features/auth/geckoAuthContext'
import {
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexPermissions,
} from 'features/auth/permissions/rolodexPermissionsList'
import { useRolodexPermissionsUserMetadata } from 'features/auth/permissions/useRolodexPermissionsUserMetadata'
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react'

/**
 * If the app is running on SecondFront, this will return true.
 * @param RUNNING_ON - the RUNNING_ON return from useRuntimeVariables()
 */
export function isRunningOnSecondFront(RUNNING_ON: string): boolean {
  return RUNNING_ON === '2f'
}

/**
 * A component which handles SecondFront authentication, providing a GeckoAuthContext
 * to the rest of the app
 * @param children        The rest of the app (this component is a simple wrapper)
 */
export const SecondFrontAuthContext: FC<{
  children: ReactNode
}> = ({ children }) => {
  const { ROLODEX_URL } = useRuntimeVariables()
  const permissions = useMemo(() => new Set<string>(), [])
  /** Return true for all permission checks except for "is a gecko" */
  const hasPermissions = useCallback(
    (toCheck: RolodexPermissions[]) =>
      toCheck.some((p) => p !== PERMISSION_GECKO_INTERNAL_MEMBER),
    []
  )
  const [showPermissionGates, setShowPermissionGates] = useState<boolean>(false)
  const authorizedAxios = useMemo(() => {
    const newAxios = rawAxios.create()
    // Make sure we pass the auth cookie with every request
    newAxios.defaults.withCredentials = true

    newAxios.interceptors.response.use(
      (response: AxiosResponse) => {
        // This is not great, but if we get a 401 from 2F, it likely
        // means the cookie expired, so we need to refresh the page
        // to trigger the login redirect
        if (response.status === HttpStatusCode.Unauthorized) {
          console.warn(
            `Response from axios call had status ${response.status}. Reload to trigger a login`
          )
          window.location.reload()
        }
        return response
      },
      (error: AxiosError) => {
        if (error?.status === HttpStatusCode.Unauthorized) {
          console.warn(error)
          console.warn(
            `Error response from axios call had status ${error?.status}. Reload to trigger a login`
          )
          window.location.reload()
        }
      }
    )

    return newAxios
  }, [])

  // Can't put this in `useRolodexPermissions` because that depends on this provider
  const { data: currentUser, mutate: refreshCurrentUser } =
    useRolodexPermissionsUserMetadata(
      authorizedAxios,
      undefined,
      ROLODEX_URL,
      false
    )

  return (
    <GeckoAuthContext.Provider
      value={{
        // Auth
        authorization: undefined,
        authorizedAxios,
        isAuthenticated: true,
        // User
        currentUser,
        refreshCurrentUser,
        // Permissions
        permissions,
        hasPermissions,
        // TODO: 2F intercepts the request and doesn't set the token, so we can't get user info.
        user: undefined,
        logout: () => {},
        showPermissionGates: showPermissionGates,
        setShowPermissionGates: setShowPermissionGates,
      }}>
      {children}
    </GeckoAuthContext.Provider>
  )
}
